import { motion } from "framer-motion";
import { Download, Users, FileText, BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";

interface AppHeaderProps {
  onExportCSV: () => void;
  onOpenParticipants: () => void;
}

export const AppHeader = ({ onExportCSV, onOpenParticipants }: AppHeaderProps) => {
  const isMobile = useIsMobile();

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="flex flex-col md:flex-row items-center justify-between mb-2 md:mb-8 gap-2 md:gap-4 bg-gradient-glass backdrop-blur-sm rounded-lg p-2 md:p-4 shadow-sm"
    >
      <div className="flex items-center gap-2 md:gap-4">
        <motion.div
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="relative"
        >
          <motion.img 
            src="/lovable-uploads/9f5d9959-966e-49e4-863a-97d5b718a5a6.png" 
            alt="TravelPocket Logo" 
            className="h-10 w-10 md:h-20 md:w-20 object-contain rounded-lg shadow-sm hover:shadow-md transition-shadow"
          />
          <motion.div
            className="absolute -top-1 -right-1 bg-gradient-secondary text-white text-[10px] md:text-xs px-1.5 md:px-2 py-0.5 rounded-full"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.5 }}
          >
            Pro
          </motion.div>
        </motion.div>
        <div className="flex flex-col">
          <motion.h1 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-lg md:text-4xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent"
          >
            TravelPocket
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-[10px] md:text-sm text-muted-foreground"
          >
            Smart Expense Tracker
          </motion.p>
        </div>
      </div>
      
      <div className="flex items-center gap-2 w-full md:w-auto justify-end">
        <HoverCard>
          <HoverCardTrigger asChild>
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} className="w-full md:w-auto">
              <Button
                onClick={onOpenParticipants}
                variant="outline"
                size={isMobile ? "sm" : "default"}
                className="flex items-center gap-2 bg-gradient-glass hover:bg-white/80 transition-colors w-full md:w-auto group"
              >
                <Users className="h-4 w-4 group-hover:text-primary transition-colors" />
                {!isMobile && "Trip Participants"}
              </Button>
            </motion.div>
          </HoverCardTrigger>
          <HoverCardContent className="w-80">
            <div className="flex justify-between space-x-4">
              <div className="space-y-1">
                <h4 className="text-sm font-semibold">Manage Participants</h4>
                <p className="text-sm text-muted-foreground">
                  Add or remove trip participants and track their expenses
                </p>
              </div>
              <Users className="h-4 w-4 text-primary animate-pulse" />
            </div>
          </HoverCardContent>
        </HoverCard>

        <HoverCard>
          <HoverCardTrigger asChild>
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} className="w-full md:w-auto">
              <Button 
                onClick={onExportCSV} 
                variant="outline"
                size={isMobile ? "sm" : "default"}
                className="flex items-center gap-2 bg-gradient-secondary text-white hover:opacity-90 transition-opacity w-full md:w-auto group"
              >
                <Download className="h-4 w-4 group-hover:scale-110 transition-transform" />
                {!isMobile && "Export CSV"}
              </Button>
            </motion.div>
          </HoverCardTrigger>
          <HoverCardContent className="w-80">
            <div className="flex justify-between space-x-4">
              <div className="space-y-1">
                <h4 className="text-sm font-semibold">Export Data</h4>
                <p className="text-sm text-muted-foreground">
                  Download your trip expenses in CSV format for detailed analysis
                </p>
              </div>
              <FileText className="h-4 w-4 text-primary animate-pulse" />
            </div>
          </HoverCardContent>
        </HoverCard>
      </div>
    </motion.div>
  );
};